import React, { type ReactNode } from 'react';

import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import { setSelectedLimitations } from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { JmrFlowTypes } from 'src/utils/jmrUtils';
import {
  type AllLimitationTypes,
  type DurationLimitationTask,
  type FreeFormLimitationTask,
  type FrequencyLimitationTask,
  isDurationLimitationTask,
  isFreeFormLimitationTask,
  isFrequencyLimitationTask,
  type LimitationOption,
} from 'src/utils/restrictionUtils';

import RestrictionItemBox from '../restrictionPageNewExperience/restrictionItemBox';

import DurationSelector from './durationSelector';
import FreeFormSelector from './freeFormSelector';
import FrequencySelector from './frequencySelector';

// **** Constants **** //

// Exporting for testing
export const TOAST_MESSAGE = 'Please see the errors below, and try clicking "Next" again';

/**
 * Common props for every restriction selector. `restrictionItem` is generic so each selector
 * receives the concrete restriction type it renders (e.g. YesNoRestrictionTask).
 */
interface SelectorProps<T extends LimitationOption = LimitationOption> {
  limitationItem: T;
  isDisabled: boolean;
  index: number;
  updateLimitationOption: (arg: T) => void;
}
/**
 * Main component.
 */
const NonPhysicalLimitations = () => {
  const dispatch = useAppDispatch();
  const { selectedLimitations } = useAppSelector((state) => state.newJmrPage);
  const { jmrFlowType } = useAppSelector((state) => state.newJmrPage);
  const isDisabled = jmrFlowType === JmrFlowTypes.UpdateDates;

  const updateLimitationOption = (updatedLimitation: AllLimitationTypes) =>
    dispatch(
      setSelectedLimitations(
        selectedLimitations.map((original) => (original.id === updatedLimitation.id ? updatedLimitation : original)),
      ),
    );

  const renderSelectors = (item: LimitationOption, index: number) => {
    const selectorProps = { isDisabled, index, updateLimitationOption };
    const selectors: ReactNode[] = [];

    if (isDurationLimitationTask(item)) {
      selectors.push(<DurationLimitation limitationItem={item} {...selectorProps} />);
    }

    if (isFrequencyLimitationTask(item)) {
      selectors.push(<FrequencyLimitation limitationItem={item} {...selectorProps} />);
    }

    if (isFreeFormLimitationTask(item)) {
      selectors.push(<FreeFormLimitation limitationItem={item} {...selectorProps} />);
    }

    return selectors.map((selector, i) => <Column key={`${item.name}_${i}`}>{selector}</Column>);
  };

  return (
    <Column spacing="400" spacingInset="400">
      {selectedLimitations.length ? (
        <Heading alignment="center" className="newJmr__restrictionPage__heading" level={4}>
          Non Physical Limitations
        </Heading>
      ) : null}
      {selectedLimitations.map((item, index) => (
        <RestrictionItemBox
          restrictionNumber={index}
          key={item.id}
          label={item.name}
          description={item.description}
          onRemoveRestriction={() =>
            dispatch(setSelectedLimitations(selectedLimitations.filter(({ id }) => id !== item.id)))
          }
        >
          <Row widths={'fill'}>{renderSelectors(item, index)}</Row>
        </RestrictionItemBox>
      ))}
    </Column>
  );
};

const FrequencyLimitation = ({
  limitationItem,
  isDisabled,
  index,
  updateLimitationOption,
}: SelectorProps<FrequencyLimitationTask>) => {
  const onChange = (newValue: number) => {
    if (+newValue !== limitationItem.frequency) {
      const { frequencyError, ...toUpdate } = limitationItem;
      toUpdate.frequency = +newValue;
      updateLimitationOption(toUpdate);
    }
  };
  return (
    <FrequencySelector
      selectedValue={limitationItem.frequency}
      onSelectValue={onChange}
      isDisabled={isDisabled}
      errorMessage={limitationItem.frequencyError}
      index={index}
    />
  );
};

const DurationLimitation = ({
  limitationItem,
  isDisabled,
  index,
  updateLimitationOption,
}: SelectorProps<DurationLimitationTask>) => {
  const onChange = (newValue: number) => {
    if (+newValue !== limitationItem.duration) {
      const { durationError, ...toUpdate } = limitationItem;
      toUpdate.duration = +newValue;
      updateLimitationOption(toUpdate);
    }
  };
  return (
    <DurationSelector
      value={limitationItem.duration}
      measureUnit={limitationItem.measureUnit}
      onChange={onChange}
      isDisabled={isDisabled}
      errorMessage={limitationItem.durationError}
      index={index}
    />
  );
};

const FreeFormLimitation = ({
  limitationItem,
  isDisabled,
  index,
  updateLimitationOption,
}: SelectorProps<FreeFormLimitationTask>) => {
  const onChange = (newValue: string) => {
    if (newValue !== limitationItem.freeFormText) {
      const { freeFormError, ...toUpdate } = limitationItem;
      toUpdate.freeFormText = newValue;
      updateLimitationOption(toUpdate);
    }
  };
  return (
    <FreeFormSelector
      value={limitationItem.freeFormText}
      onChange={onChange}
      isDisabled={isDisabled}
      errorMessage={limitationItem.freeFormError}
      index={index}
    />
  );
};

export default NonPhysicalLimitations;
